import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Activity, Filter } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const getAdminAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ActivityLogsPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchActivityLogs();
  }, [filter]);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { action: filter, limit: 100 } : { limit: 100 };
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/activity-logs`,
        { 
          headers: getAdminAuthHeader(),
          params
        }
      );
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load activity logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      created_restaurant: 'bg-green-100 text-green-700 border-green-300',
      updated_restaurant: 'bg-blue-100 text-blue-700 border-blue-300',
      deleted_restaurant: 'bg-red-100 text-red-700 border-red-300',
      suspended_restaurant: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      activated_restaurant: 'bg-green-100 text-green-700 border-green-300',
      impersonate_login: 'bg-purple-100 text-purple-700 border-purple-300',
      reset_password: 'bg-orange-100 text-orange-700 border-orange-300',
      initialized_restaurant: 'bg-teal-100 text-teal-700 border-teal-300'
    };
    return colors[action] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Activity size={28} />
                  Activity Logs
                </h1>
                <p className="text-sm text-gray-600">Track all super admin actions</p>
              </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-500" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="created_restaurant">Created Restaurant</SelectItem>
                  <SelectItem value="updated_restaurant">Updated Restaurant</SelectItem>
                  <SelectItem value="deleted_restaurant">Deleted Restaurant</SelectItem>
                  <SelectItem value="suspended_restaurant">Suspended Restaurant</SelectItem>
                  <SelectItem value="impersonate_login">Login as Restaurant</SelectItem>
                  <SelectItem value="reset_password">Reset Password</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity ({logs.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading activity logs...</p>
              </div>
            ) : logs.length > 0 ? (
              <div className="space-y-3">
                {logs.map((log, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border hover:shadow-md transition-shadow">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getActionColor(log.action)}`}>
                          {log.action.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-semibold">Admin:</span> {log.admin_email}
                        </p>
                        <p className="text-sm text-gray-900">
                          <span className="font-semibold">Resource:</span> {log.resource_type} (ID: {log.resource_id?.slice(0, 8)}...)
                        </p>
                      </div>

                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-3 p-3 bg-white rounded border">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Details:</p>
                          <div className="space-y-1">
                            {Object.entries(log.details).map(([key, value]) => (
                              <div key={key} className="text-xs text-gray-600">
                                <span className="font-medium">{key.replace(/_/g, ' ')}:</span>{' '}
                                <span className="text-gray-900">{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Activity size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-semibold mb-2">No activity logs yet</p>
                <p className="text-sm">Activity will appear here as actions are performed</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
